import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { forceVisible } from 'react-lazyload';
import { ResponsiveCarousel, CarouselItem } from '@lux-sandbox/carousel';
import { getCampaignUpcomingImage } from '@browsing/campaign';
import { ErrorBoundary } from '@browsing/components';
import { PlusTierPill, Size } from '@lux/plus-badge';
import { Translate, TranslateScope, useTranslate } from '@pegasus/translation';
import { TitlePreset } from '@lux/title';
import { useNotificationService } from '@pegasus/notification-manager';
import { useCampaignTimer } from '@browsing/hooks';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import 'dayjs/locale/nl';
import 'dayjs/locale/pl';
import 'dayjs/locale/es';

import { EA_UPCOMING_CAROUSEL_CAMPAIGNS_COUNT } from '@client/redux/modules/mylounge/constants';
import { getCategoryTabId, getEAUpcomingCampaigns } from '@client/redux/modules/mylounge';
import {
    trackSelectContent,
    trackViewPromotion,
    trackSelectCalendarAlert,
    trackDeSelectCalendarAlert,
} from '@browsing/tracking';
import {
    showHeaderErrorFlashMessage,
    showHeaderSuccessFlashMessage,
} from '@client/redux/modules/flashMessage';
import { Preset } from '@lux/text';
import { UpcomingCampaignCardMini } from '../../campaigns/components/upcoming-campaign-card-mini';
import { useLoyalty } from '../../hooks/use-loyalty';

import * as S from './ea-upcoming-carousel.styles';

export interface EAUpcomingCarouselProps {
    isMobileScreen?: boolean;
}

export const EAUpcomingCarousel: React.FC<EAUpcomingCarouselProps> = () => {
    const upcomingEACampaigns = useSelector(getEAUpcomingCampaigns);
    const loyalty = useLoyalty();
    const tier = loyalty.summary.tier;
    const categoryTabId = useSelector(getCategoryTabId);
    const dispatch = useDispatch();
    const translate = useTranslate();

    const { toggleSubscription, subscribedCampaigns } = useNotificationService();

    const firstCampaign = upcomingEACampaigns?.[0];

    const startsAt = useMemo(
        () => (firstCampaign ? new Date(firstCampaign.startsAt).getTime() : 0),
        [firstCampaign],
    );

    const { days, hours, minutes, seconds } = useCampaignTimer(startsAt);

    const withSeconds = hours < 12 && days < 1;

    const pad2 = (number: number) => `0${number}`.slice(-2);

    const formattedStartDate = useMemo(() => {
        if (!firstCampaign) return '';
        const date = dayjs(firstCampaign.startsAt);
        return date.format('ddd, D MMMM HH:mm');
    }, [firstCampaign]);

    const timerDisplay = useMemo(() => {
        if (withSeconds) {
            return `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`;
        }
        return formattedStartDate;
    }, [withSeconds, days, hours, minutes, seconds, formattedStartDate]);

    const onAddMessage = useCallback(() => {
        dispatch(showHeaderSuccessFlashMessage(translate('notifyMe.messageOnAdd')));
    }, [dispatch, translate]);

    const onErrorMessage = useCallback(() => {
        dispatch(showHeaderErrorFlashMessage(translate('secondarySubscription.errorHeader')));
    }, [dispatch, translate]);

    const handleToggleSubscription = useCallback(
        async (campaignId: string) => {
            const isCurrentlySubscribed = !!subscribedCampaigns[campaignId];
            if (!isCurrentlySubscribed) {
                trackSelectCalendarAlert('early_access_upcoming', campaignId);
            } else {
                trackDeSelectCalendarAlert('early_access_upcoming', campaignId);
            }
            try {
                await toggleSubscription(campaignId, onAddMessage);
            } catch {
                onErrorMessage();
            }
        },
        [toggleSubscription, onAddMessage, onErrorMessage, subscribedCampaigns],
    );

    const trackSwipe = useCallback(() => {
        trackSelectContent('early_access_upcoming', 'swipe');
    }, []);

    const handleScroll = useCallback(() => {
        forceVisible();
        trackSwipe();
    }, [trackSwipe]);

    // Create memoized handler for campaign view tracking
    const handleCampaignView = useCallback(
        (campaignId: string, index: number) => {
            trackViewPromotion(
                {
                    element: 'early_access_upcoming',
                    creativeName: 'early_access_upcoming',
                    itemListName: 'early_access_upcoming',
                    items: [
                        {
                            campaignIdentifier: campaignId,
                            categoryId: categoryTabId,
                            index,
                        },
                    ],
                },
                {
                    customMapper: () => ({
                        early_access_item: 1,
                    }),
                },
            );
        },
        [categoryTabId],
    );

    // Create stable handlers for each campaign to avoid creating new functions on every render
    const campaignViewHandlers = useMemo(() => {
        const handlers: Record<string, () => void> = {};
        upcomingEACampaigns?.forEach((campaign, index) => {
            handlers[campaign.campaignId] = () => handleCampaignView(campaign.campaignId, index);
        });
        return handlers;
    }, [upcomingEACampaigns, handleCampaignView]);

    if (!upcomingEACampaigns || upcomingEACampaigns.length < EA_UPCOMING_CAROUSEL_CAMPAIGNS_COUNT) {
        return null;
    }

    return (
        <ErrorBoundary componentName="EAUPCOMINGCAROUSEL">
            <TranslateScope path="plus.loyalty.eacarousel">
                <S.EAUpcomingCarouselContainer>
                    <S.DetailsWrapper>
                        <S.TierPillSection>
                            <S.TierPillWrapper>
                                <TranslateScope path="plus">
                                    <PlusTierPill tier={tier} size={Size.M}>
                                        <Translate message={`loyalty.tier.${tier}`} />
                                    </PlusTierPill>
                                    <S.EarlyAccessLabel>
                                        <Translate message="plusEarlyAccess" />
                                    </S.EarlyAccessLabel>
                                </TranslateScope>
                            </S.TierPillWrapper>
                        </S.TierPillSection>

                        <S.HeadlineSection>
                            <S.UpcomingHeadline
                                id="ea-upcoming-carousel-title"
                                preset={TitlePreset.S}
                                presetLg={TitlePreset.M}
                            >
                                <Translate message="upcomingTitle" />
                            </S.UpcomingHeadline>
                        </S.HeadlineSection>

                        <S.TimerSection>
                            <S.StartsOnLabel preset={Preset.XS}>
                                {withSeconds ? (
                                    <Translate message="startsIn" />
                                ) : (
                                    <Translate message="startsOn" />
                                )}
                            </S.StartsOnLabel>
                            <S.CountdownTimer bold preset={Preset.S}>
                                {timerDisplay}
                            </S.CountdownTimer>
                        </S.TimerSection>
                    </S.DetailsWrapper>
                    <S.EAUpcomingCarouselWrapper>
                        <ResponsiveCarousel
                            aria-labelledby="ea-upcoming-carousel-title"
                            visibleItems={2.2}
                            visibleItemsMd={3}
                            visibleItemsLg={3.2}
                            previousSlideLabel=""
                            nextSlideLabel=""
                            onScroll={handleScroll}
                            withOverflow
                        >
                            {upcomingEACampaigns.map((campaign, index) => {
                                const media = getCampaignUpcomingImage(campaign);
                                const isSubscribed = !!subscribedCampaigns[campaign.campaignId];

                                return (
                                    <CarouselItem style={{ minWidth: 0 }} key={campaign.campaignId}>
                                        <UpcomingCampaignCardMini
                                            campaign={campaign}
                                            media={media}
                                            isPlusVariant
                                            isSubscribed={isSubscribed}
                                            onToggleSubscription={handleToggleSubscription}
                                            onView={campaignViewHandlers[campaign.campaignId]}
                                        />
                                    </CarouselItem>
                                );
                            })}
                        </ResponsiveCarousel>
                    </S.EAUpcomingCarouselWrapper>
                </S.EAUpcomingCarouselContainer>
            </TranslateScope>
        </ErrorBoundary>
    );
};
